import { useAuth, useLocalStorage } from "@elit-hair/elit-tools";
import { Button, DatePicker } from "@elit-hair/elit-ui";
import { createFileRoute } from "@tanstack/react-router";
import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo } from "react";
import type { DateRange } from "react-day-picker";
import { useTranslation } from "react-i18next";
import z from "zod";

import { CallsTimeline } from "@/components/charts/calls-timeline";
import { KpiChartType } from "@/components/charts/chart-config";
import { KpiChart } from "@/components/charts/kpi-chart";
import { PageActions } from "@/components/page-actions";
import { MonthPicker, getMonthRange } from "@/components/ui/month-picker";
import { TeamMemberSelector } from "@/components/ui/team-member-selector";
import { LOCAL_STORAGE_KEYS } from "@/constants/local-storage-keys";
import { PERMISSIONS } from "@/constants/permissions";
import { useCallsForAnalytics } from "@/hooks/queries/call.queries";
import { useUserKpiByDateRange } from "@/hooks/queries/kpi.queries";
import { useMediaQuery } from "@/hooks/use-media-query";
import { cn } from "@/lib/utils";
import { useDashboardRole } from "@/pages/home/components/role-dashboard/hooks/use-dashboard-role";
import { KpiDailyOverviewTable } from "@/pages/performance/components/kpi-daily-overview-table";
import { KpiMonthSummary } from "@/pages/performance/components/kpi-month-summary";
import { KpiMonthTrendChart } from "@/pages/performance/components/kpi-month-trend-chart";
import { KpiData } from "@/types/call.types";
import { formatDateWithDayName, getDateString, getDatesInMonth } from "@/utils/format-date";
import {
    aggregateKpiData,
    buildMonthComparisonChartData,
    transformKpiToCombinedHourlyChartData,
} from "@/utils/kpi.utils";
import { APP_ROUTES_FULL, APP_ROUTES_TO } from "@/utils/routes";
import { searchValidator } from "@/utils/search-validator";

const MONTH_PARAM_FORMAT = "yyyy-MM";
const DAY_PARAM_FORMAT = "yyyy-MM-dd";

const KpisQueryParamsSchema = z.object({
    agent: z.string().optional(),
    scope: z.enum(["day", "month"]).optional(),
    month: z.string().optional(),
    day: z.string().optional(),
});

type KpisSearch = z.infer<typeof KpisQueryParamsSchema>;
type KpiScope = "day" | "month";

const EMPTY_KPI_DATA: Record<string, KpiData> = {};
const EMPTY_KPI: KpiData = {
    all: 0,
    bruttos: 0,
    netto: 0,
    averageCallDuration: 0,
    inCallTime: 0,
    callsPerHour: {},
};

const SCOPE_OPTIONS: Array<{ value: KpiScope; labelKey: string }> = [
    { value: "day", labelKey: "agent_performance_scope_day" },
    { value: "month", labelKey: "agent_performance_scope_month" },
];

export const Route = createFileRoute(APP_ROUTES_FULL.PERFORMANCE_KPIS)({
    component: PerformanceKpis,
    validateSearch: searchValidator(KpisQueryParamsSchema),
    staticData: {
        config: {
            breadcrumb: {
                title: "kpis",
                to: APP_ROUTES_TO.PERFORMANCE_KPIS,
            },
            title: "kpis",
        },
        permissions: [PERMISSIONS.CALL_KPIS_READ],
    },
});

function parseMonthParam(month?: string): DateTime {
    if (month) {
        const parsed = DateTime.fromFormat(month, MONTH_PARAM_FORMAT);
        if (parsed.isValid) {
            return parsed.startOf("month");
        }
    }

    return DateTime.now().startOf("month");
}

function parseDayParam(day?: string): string {
    if (day) {
        const parsed = DateTime.fromFormat(day, DAY_PARAM_FORMAT);
        if (parsed.isValid) {
            return getDateString(parsed);
        }
    }

    return getDateString(DateTime.now());
}

function PerformanceKpis() {
    const { t, i18n } = useTranslation();
    const { userData } = useAuth();
    const searchParams = Route.useSearch();
    const navigate = Route.useNavigate();

    const [, setLastSelectedAgent] = useLocalStorage<string | null>(
        LOCAL_STORAGE_KEYS.SELECTED_AGENT_KPI.key,
        LOCAL_STORAGE_KEYS.SELECTED_AGENT_KPI.default
    );

    const currentUserId = userData?.id ?? null;
    const { permissionCodes } = useDashboardRole();
    const canSelectTeamMember = permissionCodes.includes(PERMISSIONS.CALL_KPIS_SELECT_MEMBER);
    const isViewingOwnKpis = !canSelectTeamMember || !searchParams.agent || searchParams.agent === currentUserId;
    const selectedMember = canSelectTeamMember ? (searchParams.agent ?? currentUserId) : currentUserId;
    const selectorMemberId = isViewingOwnKpis ? null : (searchParams.agent ?? null);

    const scope: KpiScope = searchParams.scope ?? "day";
    const selectedDayDate = useMemo(() => parseDayParam(searchParams.day), [searchParams.day]);
    const comparisonDayDate = useMemo(
        () => getDateString(DateTime.fromISO(selectedDayDate).minus({ weeks: 1 })),
        [selectedDayDate]
    );

    const selectedMonth = useMemo(() => {
        if (searchParams.month) {
            return parseMonthParam(searchParams.month);
        }

        if (searchParams.day) {
            const fromDay = DateTime.fromISO(searchParams.day);
            if (fromDay.isValid) {
                return fromDay.startOf("month");
            }
        }

        return DateTime.now().startOf("month");
    }, [searchParams.month, searchParams.day]);

    const previousMonth = useMemo(() => selectedMonth.minus({ months: 1 }), [selectedMonth]);
    const days = useMemo(() => getDatesInMonth(selectedMonth), [selectedMonth]);

    const monthRange = useMemo(() => getMonthRange(selectedMonth.toJSDate()), [selectedMonth]);
    const rangeStart = useMemo(() => selectedMonth.startOf("month"), [selectedMonth]);
    const rangeEnd = useMemo(() => selectedMonth.endOf("month"), [selectedMonth]);
    const previousRangeStart = useMemo(() => previousMonth.startOf("month"), [previousMonth]);
    const previousRangeEnd = useMemo(() => previousMonth.endOf("month"), [previousMonth]);

    const dayRangeStart = useMemo(() => DateTime.fromISO(selectedDayDate).startOf("day"), [selectedDayDate]);
    const dayRangeEnd = useMemo(() => DateTime.fromISO(selectedDayDate).endOf("day"), [selectedDayDate]);
    const comparisonDayStart = useMemo(() => DateTime.fromISO(comparisonDayDate).startOf("day"), [comparisonDayDate]);
    const comparisonDayEnd = useMemo(() => DateTime.fromISO(comparisonDayDate).endOf("day"), [comparisonDayDate]);

    const currentMonthLabel = useMemo(
        () => selectedMonth.setLocale(i18n.language).toFormat("LLLL yyyy"),
        [selectedMonth, i18n.language]
    );
    const previousMonthLabel = useMemo(
        () => previousMonth.setLocale(i18n.language).toFormat("LLLL yyyy"),
        [previousMonth, i18n.language]
    );
    const comparisonDayLabel = useMemo(() => formatDateWithDayName(comparisonDayDate), [comparisonDayDate]);

    const updateSearch = useCallback(
        (updates: Partial<KpisSearch>) => {
            navigate({
                to: Route.to,
                search: (prev) => ({ ...prev, ...updates }),
                replace: true,
            });
        },
        [navigate]
    );

    useEffect(() => {
        if (canSelectTeamMember) return;
        if (!searchParams.agent) return;

        updateSearch({ agent: undefined });
    }, [canSelectTeamMember, searchParams.agent, updateSearch]);

    const handleAgentChange = (agentId: string | null) => {
        if (!canSelectTeamMember) return;

        const isOwnKpis = !agentId || agentId === currentUserId;

        if (isOwnKpis) {
            updateSearch({ agent: undefined });
            return;
        }

        updateSearch({ agent: agentId });
        setLastSelectedAgent(agentId);
    };

    const handleScopeChange = useCallback(
        (nextScope: KpiScope) => {
            if (nextScope === "day") {
                updateSearch({
                    scope: "day",
                    day: selectedDayDate,
                    month: undefined,
                });
                return;
            }

            updateSearch({
                scope: "month",
                month: selectedMonth.toFormat(MONTH_PARAM_FORMAT),
                day: undefined,
            });
        },
        [selectedDayDate, selectedMonth, updateSearch]
    );

    const handleMonthChange = useCallback(
        (range: DateRange | undefined) => {
            if (!range?.from) {
                return;
            }

            const month = DateTime.fromJSDate(range.from).startOf("month");

            updateSearch({
                scope: "month",
                month: month.toFormat(MONTH_PARAM_FORMAT),
                day: undefined,
            });
        },
        [updateSearch]
    );

    const handleDayDateChange = useCallback(
        (date: Date | undefined) => {
            if (!date) {
                return;
            }

            updateSearch({
                scope: "day",
                day: getDateString(DateTime.fromJSDate(date)),
                month: undefined,
            });
        },
        [updateSearch]
    );

    const handleDaySelect = useCallback(
        (date: string) => {
            updateSearch({
                scope: "day",
                day: date,
                month: undefined,
            });
        },
        [updateSearch]
    );

    const isMobile = !useMediaQuery("(min-width: 768px)");

    const { data: memberData = EMPTY_KPI_DATA, isLoading: isMonthLoading } = useUserKpiByDateRange(
        rangeStart,
        rangeEnd,
        selectedMember,
        undefined,
        { enabled: scope === "month" && !!selectedMember }
    );
    const { data: previousMemberData = EMPTY_KPI_DATA, isLoading: isPreviousMonthLoading } = useUserKpiByDateRange(
        previousRangeStart,
        previousRangeEnd,
        selectedMember,
        undefined,
        { enabled: scope === "month" && !!selectedMember }
    );

    const { data: dayKpiData = EMPTY_KPI_DATA } = useUserKpiByDateRange(
        dayRangeStart,
        dayRangeEnd,
        selectedMember,
        undefined,
        { enabled: scope === "day" && !!selectedMember }
    );
    const { data: comparisonDayKpiData = EMPTY_KPI_DATA } = useUserKpiByDateRange(
        comparisonDayStart,
        comparisonDayEnd,
        selectedMember,
        undefined,
        { enabled: scope === "day" && !!selectedMember }
    );

    const { data: dayCalls = [] } = useCallsForAnalytics(
        scope === "day" ? selectedDayDate : "",
        selectedMember ?? undefined
    );

    const monthTotals = useMemo(() => aggregateKpiData(memberData), [memberData]);
    const previousMonthTotals = useMemo(() => aggregateKpiData(previousMemberData), [previousMemberData]);
    const monthTrendData = useMemo(
        () => buildMonthComparisonChartData(selectedMonth, memberData, previousMemberData),
        [selectedMonth, memberData, previousMemberData]
    );

    const selectedDayKpi = dayKpiData[selectedDayDate] ?? EMPTY_KPI;
    const comparisonDayKpi = comparisonDayKpiData[comparisonDayDate] ?? EMPTY_KPI;

    const memberHourlyCallData = useMemo(() => transformKpiToCombinedHourlyChartData(selectedDayKpi), [selectedDayKpi]);

    return (
        <>
            <PageActions>
                {canSelectTeamMember ? (
                    <div className="min-w-0 w-64 sm:w-72">
                        <TeamMemberSelector
                            selectedMemberId={selectorMemberId}
                            onMemberChange={handleAgentChange}
                            currentUserId={currentUserId}
                        />
                    </div>
                ) : null}
                <div className="flex flex-wrap items-center gap-1 rounded-lg border bg-muted/30 p-1">
                    {SCOPE_OPTIONS.map((option) => (
                        <Button
                            key={option.value}
                            type="button"
                            size="sm"
                            variant={scope === option.value ? "default" : "ghost"}
                            className={cn("h-8 px-3", scope === option.value && "shadow-sm")}
                            onClick={() => handleScopeChange(option.value)}
                        >
                            {t(option.labelKey)}
                        </Button>
                    ))}
                </div>
                {scope === "day" ? (
                    <DatePicker
                        date={DateTime.fromISO(selectedDayDate).toJSDate()}
                        onDateChange={handleDayDateChange}
                    />
                ) : (
                    <MonthPicker
                        monthRange={monthRange}
                        onMonthChange={handleMonthChange}
                        maxDate={DateTime.now().endOf("month").toJSDate()}
                        buttonWidth="w-[200px]"
                    />
                )}
            </PageActions>
            <div className="h-full w-full space-y-8">
                {selectedMember && scope === "day" && (
                    <>
                        <div className="space-y-2">
                            <h2 className="text-sm font-semibold text-foreground">
                                {t("kpi_day_overview", { day: formatDateWithDayName(selectedDayDate) })}
                            </h2>
                            <KpiMonthSummary
                                kpiData={selectedDayKpi}
                                comparisonKpiData={comparisonDayKpi}
                                comparisonLabel={comparisonDayLabel}
                            />
                        </div>

                        <div className="space-y-2">
                            <h2 className="text-sm font-semibold text-foreground">{t("hourly_analytics")}</h2>
                            <div className="flex w-full flex-col gap-4">
                                <KpiChart
                                    data={memberHourlyCallData}
                                    type={KpiChartType.COMBINED_METRICS}
                                    height={isMobile ? 280 : 400}
                                />
                                <CallsTimeline calls={dayCalls} />
                            </div>
                        </div>
                    </>
                )}

                {selectedMember && scope === "month" && (
                    <>
                        <div className="space-y-2">
                            <h2 className="text-sm font-semibold text-foreground">
                                {t("kpi_month_overview", { month: currentMonthLabel })}
                            </h2>
                            <KpiMonthSummary
                                kpiData={monthTotals}
                                comparisonKpiData={previousMonthTotals}
                                comparisonLabel={previousMonthLabel}
                            />
                        </div>

                        <KpiMonthTrendChart
                            data={monthTrendData}
                            currentMonthLabel={currentMonthLabel}
                            previousMonthLabel={previousMonthLabel}
                            isLoading={isMonthLoading || isPreviousMonthLoading}
                            height={isMobile ? 280 : 360}
                        />

                        <div>
                            <h2 className="mb-3 text-sm font-semibold text-foreground">{t("daily_overview")}</h2>
                            <KpiDailyOverviewTable days={days} onDateSelect={handleDaySelect} data={memberData} />
                        </div>
                    </>
                )}
            </div>
        </>
    );
}
